import { I18n } from '@calendly/i18n'
import { createSlice } from '@reduxjs/toolkit'

import { TriggerKind } from 'shared/constants/send_from_calendly'

const initialState = {
  isOpen: false,
  eventType: null, // { uuid, name, color, locale }
  singleUseLink: null, // { bookingUrl, contact, id }
  contact: null, // { name, email }
  invitee: null, // { name, email }
  event: null,
  flowStep: null, // FlowStep
  isToFieldLocked: false,
  isInviteeOnlyReschedule: false,
  withCloseConfirmation: false,
  backUpValues: {},
  flowKind: null,
  emailLocale: null,
  prefilledSubject: null,
  prefilledBody: null,
  recipientFieldsStatus: {
    to: false,
    cc: false,
    bcc: false
  },
  triggerKind: null,
  source: null,
  trackingSource: null,
  requestId: null
}

export const FlowStep = {
  eventType: 'eventType',
  sendingForm: 'sendingForm',
  closeConfirmation: 'closeConfirmation'
}

export const FlowKind = {
  reschedule: 'reschedule',
  contact: 'contact',
  eventType: 'eventType',
  eventTypePreview: 'eventTypePreview',
  schedulingLink: 'schedulingLink'
}

const slice = createSlice({
  name: 'sendFromCalendly',
  initialState,
  reducers: {
    closeSendPopup: (state) => {
      Object.assign(state, initialState)
    },
    goBack: (state) => {
      state.flowStep = FlowStep.eventType
      state.backUpValues = {}
    },
    showConfirmation: (state, { payload: formValues }) => {
      state.flowStep = FlowStep.closeConfirmation
      state.backUpValues = formValues
    },
    backToEditing: (state) => {
      state.flowStep = FlowStep.sendingForm
    },
    selectEventType: (state, { payload: { eventType } }) => {
      state.eventType = eventType
      state.emailLocale = eventType?.locale || I18n.locale
      state.flowStep = FlowStep.sendingForm
    },
    openSendPopupForEventType: (state, { payload: { eventType, triggerKind, source } }) => {
      state.isOpen = true
      state.eventType = eventType
      state.emailLocale = eventType?.locale || I18n.locale
      state.flowStep = FlowStep.sendingForm
      state.flowKind = triggerKind === TriggerKind.bookingPage ? FlowKind.eventTypePreview : FlowKind.eventType
      state.triggerKind = triggerKind
      state.source = source
    },
    openSendPopupForSchedulingLink: (state, { payload: { singleUseLink, eventType, source } }) => {
      state.isOpen = true
      state.eventType = eventType
      state.emailLocale = eventType?.locale || I18n.locale
      state.isToFieldLocked = singleUseLink.contact?.status === 'found'
      state.contact = state.isToFieldLocked ? singleUseLink.contact : null
      state.singleUseLink = singleUseLink
      state.flowStep = FlowStep.sendingForm
      state.flowKind = FlowKind.schedulingLink
      state.triggerKind = TriggerKind.sul
      state.source = source
    },
    openSendPopupForEventRescheduling: (state, { payload: { invitee, event, isInviteeOnlyReschedule = false } }) => {
      state.isOpen = true
      state.eventType = event.eventType
      state.emailLocale = event.eventType?.locale || I18n.locale
      state.invitee = invitee
      state.event = event
      state.isToFieldLocked = true
      state.isInviteeOnlyReschedule = isInviteeOnlyReschedule
      state.flowStep = FlowStep.sendingForm
      state.flowKind = FlowKind.reschedule
      state.triggerKind = TriggerKind.reschedule
    },
    openSendPopupForGroupEventRescheduling: (state, { payload: { invitee, event, isInviteeOnlyReschedule = false } }) => {
      state.isOpen = true
      state.eventType = event.eventType
      state.emailLocale = event.eventType?.locale || I18n.locale
      state.invitee = invitee
      state.event = event
      state.isToFieldLocked = true
      state.isInviteeOnlyReschedule = isInviteeOnlyReschedule
      state.flowStep = FlowStep.sendingForm
      state.flowKind = FlowKind.reschedule
      state.triggerKind = TriggerKind.inviteeDetailsReschedule
    },
    openSendPopupForContact: (
      state,
      {
        payload: {
          contact,
          source = null,
          trackingSource = null,
          requestId = null,
          prefilledSubject = null,
          prefilledBody = null
        }
      }
    ) => {
      state.isOpen = true
      state.emailLocale = I18n.locale
      state.contact = contact
      state.withCloseConfirmation = true
      state.isToFieldLocked = true
      state.flowStep = FlowStep.eventType
      state.flowKind = FlowKind.contact
      state.triggerKind = TriggerKind.contact
      state.prefilledSubject = prefilledSubject
      state.prefilledBody = prefilledBody
      state.source = source
      state.trackingSource = trackingSource
      state.requestId = requestId
    },
    openSendPopupForContactWithEventType: (
      state,
      {
        payload: {
          contact,
          source = null,
          trackingSource = null,
          requestId = null,
          prefilledSubject = null,
          prefilledBody = null,
          eventType = null
        }
      }) => {
      state.isOpen = true
      state.emailLocale = eventType?.locale || I18n.locale
      state.contact = contact
      state.withCloseConfirmation = true
      state.isToFieldLocked = true
      state.flowStep = FlowStep.sendingForm
      state.flowKind = FlowKind.contact
      state.triggerKind = TriggerKind.contact
      state.source = source
      state.trackingSource = trackingSource
      state.requestId = requestId
      state.prefilledSubject = prefilledSubject
      state.prefilledBody = prefilledBody
      state.eventType = eventType
    },
    openSendPopupForContactsList: (state, { payload: { contact, source } }) => {
      state.isOpen = true
      state.emailLocale = I18n.locale
      state.contact = contact
      state.withCloseConfirmation = true
      state.isToFieldLocked = true
      state.flowStep = FlowStep.eventType
      state.flowKind = FlowKind.contact
      state.triggerKind = TriggerKind.contacts_list
      state.source = source
    },
    updateRecipientFieldStatus: (state, { payload: { field, status } }) => {
      state.recipientFieldsStatus[field] = status
    },
  }
})

export const {
  reducer,
  actions: {
    closeSendPopup,
    goBack,
    showConfirmation,
    backToEditing,
    selectEventType,
    openSendPopupForEventRescheduling,
    openSendPopupForContact,
    openSendPopupForContactWithEventType,
    openSendPopupForContactsList,
    updateRecipientFieldStatus,
    openSendPopupForEventType,
    openSendPopupForSchedulingLink,
    openSendPopupForGroupEventRescheduling
  }
} = slice
